import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import PredictionSignalRail from "../components/predictions/room/PredictionSignalRail";
import PredictionRoomDetail from "../components/predictions/room/PredictionRoomDetail";
import PredictionOverview from "../components/predictions/room/PredictionOverview";
import {
  buildTipsBoard,
  countTips,
  extractTipsLeagues,
  flattenBoard,
  matchesTipsFilter,
  matchesTipsSearch,
  sortFixtures,
} from "../components/predictions/desk/tipsDeskHelpers";
import {
  fetchAIPredictions,
  fetchCommunityPredictions,
  selectAIPagination,
  selectAIPredictions,
  selectAILoading,
  selectAILoadingMore,
  selectCommunityLoading,
  selectCommunityLoadingMore,
  selectCommunityPagination,
  selectCommunityPredictions,
  selectPredictionsErrors,
  selectUnlockPending,
  unlockAIPrediction,
  unlockCommunityPrediction,
} from "../redux/predictionsSlice";
import {
  fetchShowcaseP2PBets,
  selectShowcaseP2PBets,
} from "../../redux/slices/p2pShowcaseSlice";
import { selectAppIsAuthenticated, selectAppUser } from "../redux/appAuthSlice";
import { syncBondChatForUser } from "../redux/bondchat/generalChatSlice";
import { getAIUnlockPricing, isMatchStarted } from "../utils/predictionHelpers";
import {
  getFixtureId as getP2PFixtureId,
  hasOpenSpots,
  sortP2PBets,
} from "../utils/p2pHelpers";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { usePersistedPanelSize } from "../hooks/usePersistedPanelSize";
import { useHorizontalPanelResize } from "../hooks/useHorizontalPanelResize";
import "../predictions.css";
import "../tips-cards.css";
import "../prediction-room.css";

const FOCUS_QUERY = "f";
const SOURCE_QUERY = "src";
const TIP_QUERY = "t";
const MOBILE_BREAKPOINT = "(max-width: 900px)";
const INDEX_DEFAULT = 320;
const INDEX_MIN = 280;
const INDEX_MAX = 460;

const Predictions = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const selectedId = params.get(FOCUS_QUERY) || null;
  const sourceParam = params.get(SOURCE_QUERY) || null;
  const tipParam = params.get(TIP_QUERY) || null;

  const community = useSelector(selectCommunityPredictions);
  const ai = useSelector(selectAIPredictions);
  const communityLoading = useSelector(selectCommunityLoading);
  const aiLoading = useSelector(selectAILoading);
  const communityLoadingMore = useSelector(selectCommunityLoadingMore);
  const aiLoadingMore = useSelector(selectAILoadingMore);
  const communityPagination = useSelector(selectCommunityPagination);
  const aiPagination = useSelector(selectAIPagination);
  const p2pBets = useSelector(selectShowcaseP2PBets);
  const errors = useSelector(selectPredictionsErrors);
  const unlockPending = useSelector(selectUnlockPending);
  const user = useSelector(selectAppUser);
  const isAuthenticated = useSelector(selectAppIsAuthenticated);

  const [filter, setFilter] = useState("all");
  const [sortBy, setSortBy] = useState("signal");
  const [leagueFilter, setLeagueFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [toast, setToast] = useState(null);

  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const {
    size: indexWidth,
    setClampedSize: setIndexWidth,
    reset: resetIndexWidth,
    minSize: indexMin,
    maxSize: indexMax,
  } = usePersistedPanelSize("bb-pr-index-w", {
    defaultSize: INDEX_DEFAULT,
    minSize: INDEX_MIN,
    maxSize: INDEX_MAX,
  });
  const onIndexResizePointerDown = useHorizontalPanelResize(
    indexWidth,
    setIndexWidth,
    "start"
  );

  const loadCommunity = useCallback(
    (page = 1, append = false) =>
      dispatch(fetchCommunityPredictions({ page, limit: 20, append })),
    [dispatch]
  );
  const loadAI = useCallback(
    (page = 1, append = false) =>
      dispatch(fetchAIPredictions({ page, limit: 20, append })),
    [dispatch]
  );

  useEffect(() => {
    loadCommunity(1, false);
    loadAI(1, false);
    dispatch(fetchShowcaseP2PBets({ page: 1, limit: 30 }));
  }, [dispatch, loadCommunity, loadAI]);

  useEffect(() => {
    if (user?._id) dispatch(syncBondChatForUser(user._id));
  }, [dispatch, user?._id]);

  useEffect(() => {
    if (!location.state?.toast) return;
    setToast(location.state.toast);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = window.setTimeout(() => setToast(null), 5000);
    return () => window.clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    const unlockErr = errors.unlock?.error || errors.unlock?.message || errors.unlock;
    if (!unlockErr) return;
    const raw = typeof unlockErr === "string" ? unlockErr : "Unlock failed. Please try again.";
    const message = /insufficient coin/i.test(raw)
      ? "Not enough COIN to unlock this tip. Top up in Bond Coin, then try again."
      : raw;
    setToast({ message });
  }, [errors.unlock]);

  const baseSections = useMemo(
    () => buildTipsBoard(community, ai, user?._id),
    [community, ai, user?._id]
  );
  const totals = useMemo(() => countTips(baseSections), [baseSections]);
  const leagues = useMemo(() => extractTipsLeagues(baseSections), [baseSections]);

  const sections = useMemo(() => {
    const filterCtx = {
      userId: user?._id,
      favoriteTeam: user?.favoriteTeam,
      followingIds: user?.following || [],
    };
    return baseSections
      .filter((s) => !leagueFilter || s.leagueId === leagueFilter)
      .map((s) => {
        const fixtures = sortFixtures(
          s.fixtures.filter(
            (entry) =>
              matchesTipsFilter(entry, filter, filterCtx) &&
              matchesTipsSearch(entry, searchQuery)
          ),
          sortBy
        );
        const aiCount = fixtures.filter((f) => f.ai).length;
        const crowdCount = fixtures.reduce((n, f) => n + f.consensus.count, 0);
        return { ...s, fixtures, aiCount, crowdCount };
      })
      .filter((s) => s.fixtures.length > 0);
  }, [baseSections, filter, searchQuery, leagueFilter, sortBy, user?._id, user?.favoriteTeam, user?.following]);

  const visibleFixtures = useMemo(() => flattenBoard(sections), [sections]);
  const filteredCount = visibleFixtures.length;

  const selectedEntry = useMemo(() => {
    if (!selectedId) return null;
    return (
      flattenBoard(baseSections).find((f) => f.fixtureId === selectedId) || null
    );
  }, [baseSections, selectedId]);

  // Shared community tip links (`?t=`) resolve the fixture focus when needed.
  useEffect(() => {
    if (!tipParam || selectedId) return;
    const tipId = String(tipParam);
    for (const entry of flattenBoard(baseSections)) {
      const hit = (entry.community || []).some((t) => String(t._id) === tipId);
      if (hit) {
        const next = new URLSearchParams(params);
        next.set(FOCUS_QUERY, entry.fixtureId);
        if (!next.get(SOURCE_QUERY)) next.set(SOURCE_QUERY, "crowd");
        setParams(next, { replace: true });
        return;
      }
    }
  }, [tipParam, selectedId, baseSections, params, setParams]);

  const selectedP2POffers = useMemo(() => {
    if (!selectedEntry?.fixtureId) return [];
    const fixtureId = String(selectedEntry.fixtureId);
    return sortP2PBets(
      (p2pBets || []).filter(
        (bet) => String(getP2PFixtureId(bet) || "") === fixtureId && hasOpenSpots(bet)
      ),
      "recent"
    );
  }, [p2pBets, selectedEntry?.fixtureId]);

  const setSelected = useCallback(
    (id) => {
      const next = new URLSearchParams(params);
      if (id) next.set(FOCUS_QUERY, id);
      else next.delete(FOCUS_QUERY);
      setParams(next, { replace: true });
    },
    [params, setParams]
  );

  // No auto-select: with nothing selected the desk lands on Overview.
  const onSelect = useCallback((id) => setSelected(id), [setSelected]);

  const onBackToList = useCallback(() => setSelected(null), [setSelected]);

  const onLeagueChange = useCallback(
    (nextLeagueId) => {
      setLeagueFilter(nextLeagueId);
      if (isMobile) setSelected(null);
    },
    [isMobile, setSelected]
  );

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    setSearchQuery("");
    await Promise.all([loadCommunity(1, false), loadAI(1, false)]);
    setRefreshing(false);
  }, [loadCommunity, loadAI]);

  const loading = communityLoading || aiLoading;
  const loadingMore = communityLoadingMore || aiLoadingMore;
  const aiTotal = Number(aiPagination.totalItems) || 0;
  const communityTotal = Number(communityPagination.totalPredictions) || 0;
  const aiHasMore =
    Boolean(aiPagination.hasMore) && (!aiTotal || ai.length < aiTotal);
  const communityHasMore =
    Boolean(communityPagination.hasNextPage) &&
    (!communityTotal || community.length < communityTotal);
  const hasMore =
    !loading &&
    (aiHasMore || communityHasMore);

  const onLoadMore = useCallback(async () => {
    if (loadingMore) return;

    const tasks = [];
    if (communityHasMore) {
      tasks.push(loadCommunity(Number(communityPagination.page || 1) + 1, true));
    }
    if (aiHasMore) {
      tasks.push(loadAI(Number(aiPagination.currentPage || 1) + 1, true));
    }
    if (tasks.length) await Promise.all(tasks);
  }, [
    loadingMore,
    communityHasMore,
    communityPagination.page,
    aiHasMore,
    aiPagination.currentPage,
    loadCommunity,
    loadAI,
  ]);

  const handleUnlock = useCallback(
    (id) => {
      if (!isAuthenticated) return;
      if (selectedEntry && isMatchStarted(selectedEntry.fixture)) return;
      dispatch(unlockAIPrediction(id));
    },
    [dispatch, isAuthenticated, selectedEntry]
  );

  const handleUnlockCommunity = useCallback(
    (predictionId) => {
      if (!isAuthenticated || !user?._id) {
        setToast({ message: "Log in to unlock premium tips." });
        return;
      }
      if (selectedEntry && isMatchStarted(selectedEntry.fixture)) {
        setToast({ message: "Cannot unlock tips after the match has started." });
        return;
      }
      dispatch(syncBondChatForUser(user._id));
      dispatch(unlockCommunityPrediction({ predictionId, userId: user._id }));
    },
    [dispatch, isAuthenticated, selectedEntry, user?._id]
  );

  const pricing = useMemo(() => getAIUnlockPricing(user), [user]);

  const error = errors.ai || errors.community;

  const shellClassName = [
    "pr-room-shell",
    isMobile ? "pr-room-shell-mobile" : "",
    isMobile && selectedId ? "pr-room-shell-has-detail" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const gridStyle = !isMobile ? { "--pr-index-w": `${indexWidth}px` } : undefined;

  return (
    <div className={shellClassName}>
      {toast ? (
        <div className="pr-toast" role="status">
          {toast.message}
        </div>
      ) : null}

      {error && !loading ? (
        <div className="pr-error" role="alert">
          Could not load tips.{" "}
          <button
            type="button"
            onClick={() => {
              loadCommunity(1, false);
              loadAI(1, false);
            }}
          >
            Retry
          </button>
        </div>
      ) : null}

      <div className="pr-room-grid" style={gridStyle}>
        <div className="pr-room-rail-pane">
          <PredictionSignalRail
            sections={sections}
            totals={totals}
            filteredCount={filteredCount}
            selectedId={selectedId}
            onSelect={onSelect}
            filter={filter}
            onFilterChange={setFilter}
            sortBy={sortBy}
            onSortChange={setSortBy}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            leagues={leagues}
            leagueFilter={leagueFilter}
            onLeagueChange={onLeagueChange}
            refreshing={refreshing}
            onRefresh={onRefresh}
            isLoading={loading}
            hasMore={hasMore}
            loadingMore={loadingMore}
            onLoadMore={onLoadMore}
            isAuthenticated={isAuthenticated}
            favoriteTeam={user?.favoriteTeam}
          />
          {!isMobile ? (
            <div
              className="pr-resize-handle"
              role="separator"
              aria-orientation="vertical"
              aria-valuenow={indexWidth}
              aria-valuemin={indexMin}
              aria-valuemax={indexMax}
              aria-label="Resize tips board"
              title="Drag to resize · double-click to reset"
              onPointerDown={onIndexResizePointerDown}
              onDoubleClick={resetIndexWidth}
            />
          ) : null}
        </div>

        {selectedEntry ? (
          <PredictionRoomDetail
            entry={selectedEntry}
            user={user}
            userId={user?._id}
            p2pOffers={selectedP2POffers}
            onUnlock={handleUnlock}
            onUnlockCommunity={handleUnlockCommunity}
            unlockPending={unlockPending}
            pricing={pricing}
            isAuthenticated={isAuthenticated}
            onBackToList={isMobile ? onBackToList : undefined}
            onBackToOverview={!isMobile ? onBackToList : undefined}
            initialSource={
              sourceParam === "crowd" || sourceParam === "ai"
                ? sourceParam
                : tipParam
                  ? "crowd"
                  : null
            }
            focusTipId={tipParam}
            onNotify={setToast}
          />
        ) : (
          <PredictionOverview
            fixtures={visibleFixtures}
            totals={totals}
            selectedId={selectedId}
            onSelect={onSelect}
            isAuthenticated={isAuthenticated}
          />
        )}
      </div>
    </div>
  );
};

export default Predictions;
